import type { Asset } from '@/types/oscar';
import { formatDate } from '@/lib/dateFormat';

/** Columns exported by the Assets register CSV, in order. */
const HEADERS = [
  'Serial / ICCID', 'Hostname', 'Asset tag', 'Category', 'Make', 'Model',
  'Status', 'Location', 'Held by', 'Previously held by',
  'Mobile number', 'Intune', 'IMEI', 'Warranty expiry', 'Notes',
] as const;

/** RFC-4180-style field escaping: quote when the value contains a comma, quote or newline. */
function escapeCsv(value: unknown): string {
  const s = value == null ? '' : String(value);
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function imeiOf(a: Asset): string {
  // Prefer the IMEI synced from Intune; fall back to the manual Attributes value.
  const v = a.IntuneImei ?? a.Attributes?.IMEI;
  return v == null ? '' : String(v);
}

/**
 * Builds the Assets register CSV from the (already filtered) row set. Pure so it
 * can be unit-tested; the page feeds it every page of the current filter result.
 * Location ids are resolved to their description via the same map the table uses.
 */
export function buildAssetsCsv(rows: Asset[], locationName: Map<number, string>): string {
  const lines = [HEADERS.join(',')];
  for (const a of rows) {
    const loc = a.LocationId != null ? (locationName.get(a.LocationId) ?? `#${a.LocationId}`) : '';
    lines.push([
      a.SerialNumber ?? '',
      a.Hostname ?? '',
      a.AssetTag ?? '',
      a.Category,
      a.Make ?? '',
      a.Model ?? '',
      a.LifecycleState,
      loc,
      a.HeldByPersonName ?? '',
      a.PriorUser ?? '',
      a.Msisdn ?? '',
      a.IntuneStatus ?? '',
      imeiOf(a),
      a.WarrantyExpiry ? formatDate(a.WarrantyExpiry) : '',
      a.Notes ?? '',
    ].map(escapeCsv).join(','));
  }
  return lines.join('\r\n');
}
